// One game's stored scan as an object: the engine's reading of each position
// and its verdict on each move, asked by ply. It wraps the plain `Scan` the
// analysis API stores and never changes it; when the scan grows, the review
// session makes a new `GameScan` over the new one.
import { DEFAULT_MISTAKE_RULES, type Color, type Move, type MoveKind, type Uci, type Line as EngineLine, type MistakeRules, type MoveJudgement, type PositionAnalysis, type Scan } from '@chess/api-model';
import { judgeMove, judgementFrom, positionAt } from '@chess/api-analysis';
import { Score } from './score.ts';
import { moveGradeOf, type MoveGrade } from './move-grade.ts';

/** A winning line is this many centipawns ahead of the second best, for a miss (the default rules). */
export const MISS_GAP_CP = DEFAULT_MISTAKE_RULES.missGapPawns * 100;

/**
 * A miss: the position held one clearly winning line, a mate the second line
 * is not, or a lead of at least `gapCp` (the rules' `missGapPawns`) over it
 * while not behind, and `played` was something else. Scores are for the side
 * to move.
 */
export function isMissOf(lines: EngineLine[] | undefined, played: Uci | undefined, gapCp = MISS_GAP_CP): boolean {
  const [best, second] = lines ?? [];
  if (!best || !second || !played || played === best.uci) return false;
  if (best.score.mate !== undefined) {
    if (best.score.mate <= 0) return false;
    return !(second.score.mate !== undefined && second.score.mate > 0);
  }
  const score = best.score.cp ?? 0;
  if (score < 0) return false;
  if (second.score.mate !== undefined) return second.score.mate < 0;
  return score - (second.score.cp ?? 0) >= gapCp;
}

/** After a miss the mover may stand at worst this many centipawns down (the default rules); below that the move lost the position and is a blunder. */
export const MISS_HOLD_CP = DEFAULT_MISTAKE_RULES.missHoldPawns * 100;

/**
 * Whether a judged move is a blunder under the rules: it gave up at least
 * `blunderPawns` against the engine's best line. The engine's own move never
 * is. Read from the judgement's `delta`, so changing the rules needs no new
 * scan.
 */
export function isBlunderUnder(judgement: MoveJudgement, rules: MistakeRules = DEFAULT_MISTAKE_RULES): boolean {
  return judgement.uci !== judgement.best && isBlunderLoss(judgement.delta, rules);
}

/** Whether giving up this many centipawns against the best line is a blunder under the rules: a live search's loss as well as a stored judgement's. */
export function isBlunderLoss(deltaCp: number, rules: MistakeRules = DEFAULT_MISTAKE_RULES): boolean {
  return deltaCp >= rules.blunderPawns * 100;
}

/**
 * What a move was, if anything worth listing. A miss passes over a clearly
 * winning line yet leaves the mover no worse than `missHoldPawns`: the win
 * was missed, the game was not thrown away. A blunder gives up at least
 * `blunderPawns` and leaves the mover worse than that, or gives it up where
 * no winning line existed. Every miss also gives up something against the
 * best line, so the miss test must come first, or each one reads as a
 * blunder.
 *
 * `judgement` is the scan's verdict on the move, `lines` the engine's lines
 * from the position before it, `after` the score after the move from the
 * mover's side, `rules` the user's (the defaults when left out).
 */
export function moveKindOf(
  judgement: MoveJudgement | undefined,
  lines: EngineLine[] | undefined,
  played: Uci | undefined,
  after: Score | undefined,
  rules: MistakeRules = DEFAULT_MISTAKE_RULES,
): MoveKind | undefined {
  if (!judgement) return undefined;
  if (isMissOf(lines, played, rules.missGapPawns * 100) && after && after.centipawns() >= rules.missHoldPawns * 100) return 'miss';
  return isBlunderUnder(judgement, rules) ? 'blunder' : undefined;
}

/** What the verdicts need of a move: what was played. */
type PlayedMove = Pick<Move, 'uci' | 'san'>;

export class GameScan {
  readonly scan: Scan;
  /** The rules its verdicts follow: the user's, or the defaults. */
  readonly rules: MistakeRules;
  private readonly byPly = new Map<number, PositionAnalysis>();

  constructor(scan: Scan, rules: MistakeRules = DEFAULT_MISTAKE_RULES) {
    this.scan = scan;
    this.rules = rules;
    for (const p of scan.positions) this.byPly.set(p.ply, p);
  }

  get gameId(): string {
    return this.scan.gameId;
  }

  get complete(): boolean {
    return this.scan.complete;
  }

  get depth(): number {
    return this.scan.depth;
  }

  /** How many positions are read, and how many the game has. */
  get progress(): { done: number; total: number } {
    return { done: this.scan.positions.length, total: this.scan.total };
  }

  /** The engine's reading of the position after `ply` moves. */
  position(ply: number): PositionAnalysis | undefined {
    return this.byPly.get(ply) ?? positionAt(this.scan, ply);
  }

  has(ply: number): boolean {
    return this.position(ply) !== undefined;
  }

  /** The score of the position after `ply` moves, from `side`. The start of a game is level. */
  scoreAt(ply: number, side: Color): Score | undefined {
    if (ply === 0) return Score.level();
    const p = this.position(ply);
    return p ? Score.at(p, side) : undefined;
  }

  /**
   * The verdict on the move at a 1-based ply: the stored one when the scan
   * is complete, otherwise worked out from the two positions around it once
   * both are read.
   */
  judgement(ply: number, move: PlayedMove | undefined): MoveJudgement | undefined {
    const stored = judgementFrom(this.scan, ply - 1);
    if (stored) return stored;
    const before = this.position(ply - 1);
    const after = this.position(ply);
    return before && after && move ? judgeMove(before, after, move, ply) : undefined;
  }

  /** The move at a 1-based ply passed over a clearly winning line, under the rules' gap. */
  isMiss(ply: number, move: PlayedMove | undefined): boolean {
    return isMissOf(this.position(ply - 1)?.lines, move?.uci, this.rules.missGapPawns * 100);
  }

  /** Blunder, miss or neither for the move at a 1-based ply, played by `mover`. See `moveKindOf`. */
  kind(ply: number, move: PlayedMove | undefined, mover: Color): MoveKind | undefined {
    return moveKindOf(this.judgement(ply, move), this.position(ply - 1)?.lines, move?.uci, this.scoreAt(ply, mover), this.rules);
  }

  /** The app's grade of the move at a 1-based ply, played by `mover` (`moveGradeOf`): the rules' blunder or miss, else the scan's own grade. */
  grade(ply: number, move: PlayedMove | undefined, mover: Color): MoveGrade | undefined {
    const judgement = this.judgement(ply, move);
    return judgement ? moveGradeOf(judgement, this.kind(ply, move, mover)) : undefined;
  }
}
